import { valibotResolver } from "@hookform/resolvers/valibot";
import { PlusCircle } from "@phosphor-icons/react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";

import PermissionsSelection from "common/components/PermissionsSelection";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import Label from "common/components/ui/Label";
import Switch from "common/components/ui/Switch";
import { HTTPError } from "common/helpers/HTTP";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { UserPermissions } from "common/types";

import useAgencyClientsMutations from "../../datahooks/useAgencyClientsMutations";
import useAgencySettings from "../../datahooks/useAgencySettings";
import { AgencyClientSchema, type AgencyClientSchemaType } from "../../schema";

const DEFAULT_PERMISSIONS = [
  "campaigns_limited",
  "campaigns_full",
  "leads_full",
  "inbox_limited",
  "inbox_full",
  "templates_limited",
  "templates_full",
  "accounts_limited",
  "accounts_full",
] as const;

export default function NewClientButton({ size }: { size: "sm" | "md" }) {
  const isDesktop = useTwBreakpoint("lg");
  const [isOpen, setIsOpen] = useState(false);
  const [clientData, setClientData] = useState<AgencyClientSchemaType>(null);
  const [permissions, setPermissions] = useState<UserPermissions>([
    ...DEFAULT_PERMISSIONS,
  ]);

  const { agencySettings } = useAgencySettings();
  const {
    addAgencyClient,
    isAddingAgencyClient,
    addAgencyClientError,
    resetAgencyClientError,
  } = useAgencyClientsMutations();

  const {
    reset: resetForm,
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AgencyClientSchemaType>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      skipLoginEmail: false,
    },
    resolver: valibotResolver(AgencyClientSchema),
  });

  function resetStates() {
    resetForm();
    setClientData(null);
    resetAgencyClientError();
    setPermissions([...DEFAULT_PERMISSIONS]);
  }

  function onNext(formData: AgencyClientSchemaType) {
    setClientData(formData);
  }

  function onSubmit() {
    addAgencyClient({
      client: clientData,
      permissions,
    }).then(() => {
      setIsOpen(false);
      resetStates();
    });
  }

  let errorMessage: string;
  if (addAgencyClientError instanceof HTTPError) {
    errorMessage =
      addAgencyClientError.type === "ConflictError"
        ? "Client is already part of this workspace"
        : addAgencyClientError.serverMessage;
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Close = isDesktop ? DialogClose : DrawerClose;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  return (
    <Component
      open={isOpen}
      onOpenChange={setIsOpen}
      onAfterClose={resetStates}
    >
      <Trigger asChild>
        <Button
          leftIcon={<PlusCircle weight="fill" />}
          size={size}
          className="only:grow"
          disabled={!agencySettings}
        >
          Invite client
        </Button>
      </Trigger>

      <Content>
        <Header
          title={
            clientData
              ? `Set permissions for ${clientData.email}`
              : "Invite a client"
          }
          subtitle={
            clientData
              ? "Give this client full access to the workspace, or limit which pages they can manage"
              : "Invite an agency client to this workspace. They will get an email with a link to join."
          }
        />

        <form
          onSubmit={handleSubmit(clientData ? onSubmit : onNext)}
          className="flex w-full flex-col"
        >
          <Body>
            {clientData ? (
              <>
                <PermissionsSelection
                  permissions={permissions}
                  setPermissions={setPermissions}
                  isClient
                />
                {errorMessage && (
                  <p className="mx-3 mt-3 text-sm text-red-500">
                    {errorMessage}
                  </p>
                )}
              </>
            ) : (
              <>
                <FormField
                  label="First name"
                  htmlFor="firstName"
                  error={errors.firstName?.message}
                  className="mb-3"
                >
                  <Input
                    id="firstName"
                    placeholder="Enter first name"
                    {...register("firstName")}
                  />
                </FormField>

                <FormField
                  label="Last name"
                  htmlFor="lastName"
                  error={errors.lastName?.message}
                  className="mb-3"
                >
                  <Input
                    id="lastName"
                    placeholder="Enter last name"
                    {...register("lastName")}
                  />
                </FormField>

                <FormField
                  label="Email"
                  htmlFor="email"
                  error={errors.email?.message}
                  className="mb-3"
                >
                  <Input
                    id="email"
                    placeholder="Enter client email"
                    {...register("email")}
                  />
                </FormField>

                <div className="flex justify-between border-y border-y-black-100 py-4 items-center">
                  <div className="flex flex-col gap-y-1 items-start">
                    <Label htmlFor="skipLoginEmail">Send login email</Label>
                    <span className="text-xs-medium text-black-500 ml-1">
                      Automatically send an email to invited clients
                    </span>
                  </div>
                  <Controller
                    control={control}
                    name="skipLoginEmail"
                    render={({ field }) => (
                      <Switch
                        id="skipLoginEmail"
                        color="black"
                        checked={!field.value}
                        onCheckedChange={(checked) => field.onChange(!checked)}
                      />
                    )}
                  />
                </div>
              </>
            )}
          </Body>

          <Footer className="justify-end">
            <Close asChild>
              <Button variant="secondary-black" disabled={isAddingAgencyClient}>
                Cancel
              </Button>
            </Close>

            <Button type="submit" isLoading={isAddingAgencyClient}>
              {clientData ? "Invite" : "Next"}
            </Button>
          </Footer>
        </form>
      </Content>
    </Component>
  );
}
